/**
 * =============================================================================
 * PayoutDetailSheet — Sheet lateral de detalhes de um Pagamento (Indicação)
 *
 * Padronizado 1:1 com `ContractDetailSheet` (módulo Contratos):
 *   - Sheet à direita, largura `w-[480px]` em desktop e fullscreen no mobile.
 *   - Header com chip de ícone + nome do afiliado + UUID curto + badge status.
 *   - Barra de ações contextuais (aprovar, marcar como pago, marcar como falho,
 *     cancelar) — reaproveita os MESMOS handlers usados pelo dropdown da linha.
 *   - Blocos: Informações gerais, Dados de pagamento, Afiliado, Comissões
 *     vinculadas (clicáveis), Anexos/Comprovantes e Timeline cronológica.
 *   - Realtime via `useReferralPayoutDetails` (assina `affiliate_payouts`
 *     e `affiliate_commissions`).
 *
 * Segurança/UX:
 *   - UUIDs sempre renderizados em 8 chars maiúsculos, sem tooltip que exponha
 *     o UUID completo no DOM (alinhado à regra v1.2.0 do PayoutsTableView).
 *
 * @version 1.5.1
 * RELEASE:
 *   2026-04-30 v1.0.0 - Criação espelhando ContractDetailSheet.
 *   2026-04-30 v1.1.0 - Refino visual completo (header premium, cards, action bar).
 *   2026-04-30 v1.1.1 - Reconciliação do snapshot `no_pix_on_affiliate` com o
 *                       estado atual do afiliado.
 *   2026-04-30 v1.2.0 - Varredura de confiabilidade:
 *                       1) Suprime linha "Origem" quando o valor é sentinel
 *                          interno (ex.: no_pix_on_affiliate). Sentinels
 *                          continuam acionando flags (missingPix) mas não
 *                          competem com o estado atual do afiliado.
 *                       2) Suprime incondicionalmente notes do sistema
 *                          redundantes (lista SYSTEM_NOTES_TO_HIDE).
 *                       3) Allowlist DISPLAYABLE_PD_KEYS para a lista
 *                          principal; chaves desconhecidas vão para um
 *                          bloco recolhido de auditoria (<details>).
 *                       4) Fallback "Afiliado removido" quando data.affiliate
 *                          é nulo; "Negócio removido" no card de comissão.
 *                       5) Callout de falha só aparece quando status === 'failed'.
 *   2026-04-30 v1.4.0 - Comissões vinculadas: tabela com altura fixa
 *                       (max-h-[280px]) + scroll interno, <thead> sticky,
 *                       <colgroup> com larguras dedicadas, zebra leve nas
 *                       linhas, tipografia do Cliente promovida (text-sm
 *                       medium + doc abaixo) e rodapé compacto com contagem
 *                       e soma dos rewards. Resolve quebra visual quando
 *                       há muitas comissões num mesmo pagamento.
 *   2026-04-30 v1.5.0 - Comissões vinculadas: redesign premium completo.
 *                       Substitui tabela densa por lista de cards com chip
 *                       de ícone financeiro, borda esquerda colorida, valor
 *                       em destaque e ref. menor à direita. Sanitização do
 *                       nome do cliente (cleanCustomerName) remove telefone
 *                       concatenado vindo de leads. Badge "Lead" diferencia
 *                       origem pré-cliente. Rodapé sticky-bottom com soma
 *                       agregada destacada em primary.
 *   2026-04-30 v1.5.1 - Cliente com paridade de affordance vs Negócio:
 *                       cor primary, hover:underline e ícone ExternalLink
 *                       quando customer_id está presente. Não-clicáveis
 *                       seguem em foreground neutro para reforçar o contraste.
 * =============================================================================
 */
import React from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Wallet,
  CheckCircle2,
  XCircle,
  PlayCircle,
  Clock,
  Loader2,
  Ban,
  AlertCircle,
  History,
  User as UserIcon,
  Briefcase,
  ExternalLink,
  StickyNote,
  CreditCard,
  Receipt,
  Mail,
  KeyRound,
  Info,
  Sparkles,
  FileText,
  CircleDollarSign,
} from 'lucide-react';
import { PayoutAttachmentsManager } from '@/components/referral/PayoutAttachmentsManager';
import { PayoutStatusBadge } from '@/components/referral/PayoutStatusBadge';
import {
  useReferralPayoutDetails,
  type PayoutDetailsCommission,
  type PayoutActionLog,
  type PayoutStatus,
  type PayoutMethod,
} from '@/hooks/useReferralPayoutDetails';

// ============================================================================
// Tipagem de ações (reaproveita o set do PayoutsTableView)
// ============================================================================

export type PayoutDetailAction =
  | 'approve'
  | 'mark_paid'
  | 'mark_failed'
  | 'cancel';

interface PayoutDetailSheetProps {
  /** ID do payout — `null` mantém o Sheet fechado. */
  payoutId: string | null;
  /** Callback de fechamento do Sheet. */
  onClose: () => void;
  /** Aciona um item de menu (mapeado nos diálogos da página). */
  onAction: (payoutId: string, action: PayoutDetailAction) => void;
  /** Abre o modal premium do negócio (na seção "Comissões vinculadas"). */
  onOpenDeal?: (dealId: string) => void;
  /** Abre o modal de detalhes do cliente (na seção "Comissões vinculadas"). */
  onOpenCustomer?: (customerId: string) => void;
}

// ============================================================================
// CONSTANTES DE STATUS / MÉTODO
// ----------------------------------------------------------------------------
// v1.1.0: paleta da badge migrou para tons "soft" (fundo claro + texto saturado)
// — visual mais sóbrio e premium, sem perder a leitura semântica.
// ============================================================================

const STATUS_CONFIG: Record<PayoutStatus, { label: string; color: string; dot: string }> = {
  pending:    { label: 'Pendente',    color: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900/60', dot: 'bg-amber-500' },
  processing: { label: 'Processando', color: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-900/60',       dot: 'bg-blue-500' },
  paid:       { label: 'Pago',        color: 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900/60', dot: 'bg-emerald-500' },
  failed:     { label: 'Falhou',      color: 'bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-900/60',       dot: 'bg-rose-500' },
  cancelled:  { label: 'Cancelado',   color: 'bg-muted text-muted-foreground border-border',                                                                  dot: 'bg-muted-foreground' },
};

const METHOD_LABEL: Record<PayoutMethod, string> = {
  pix: 'PIX',
  bank_transfer: 'Transferência bancária',
  manual: 'Manual',
  credits: 'Crédito',
};

/**
 * Dicionário amigável das chaves do JSONB `payment_details`.
 *
 * Permite que o usuário leia "Observação" em vez de `note`, "Origem" em vez
 * de `source`, etc. Chaves desconhecidas caem no fallback `humanizeKey`.
 *
 * Inclui também tradução de VALORES sentinelas conhecidos (ex.:
 * `no_pix_on_affiliate`) para mensagens compreensíveis.
 */
const PAYMENT_DETAILS_LABELS: Record<string, string> = {
  note: 'Observação',
  source: 'Origem',
  pix_key: 'Chave PIX',
  pix_key_type: 'Tipo de chave PIX',
  bank: 'Banco',
  agency: 'Agência',
  account: 'Conta',
  account_type: 'Tipo de conta',
  holder: 'Titular',
  holder_document: 'Documento do titular',
  reference: 'Referência',
  external_id: 'ID externo',
};

/**
 * Tradução amigável de valores sentinelas conhecidos.
 * v1.2.0: adicionados `affiliate_profile_snapshot` e `manual_input` para
 * evitar exibir jargão técnico na coluna "Origem" do card de pagamento.
 */
const PAYMENT_DETAILS_VALUE_LABELS: Record<string, string> = {
  no_pix_on_affiliate: 'Afiliado sem PIX cadastrado',
  manual_creation: 'Geração manual',
  from_commissions_screen: 'Tela de Comissões',
  auto: 'Automático',
  affiliate_profile_snapshot: 'Cadastro do afiliado',
  manual_input: 'Informado manualmente',
};

/**
 * Allowlist de chaves "exibíveis" do payment_details (v1.2.0).
 * Apenas estas aparecem na seção principal de Dados de pagamento.
 * Demais chaves vão para o bloco recolhido de auditoria.
 */
const DISPLAYABLE_PD_KEYS = new Set<string>([
  'pix_key',
  'pix_key_type',
  'bank',
  'agency',
  'account',
  'account_type',
  'holder',
  'holder_document',
  'reference',
  'external_id',
]);

/**
 * Valores-sentinela internos (telemetria do backend) que NÃO devem virar
 * UI visível como "Origem". São úteis apenas para acionar lógica
 * (ex.: `missingPix`) e auditoria.
 */
const SENTINEL_VALUES = new Set<string>([
  'no_pix_on_affiliate',
  'manual_creation',
  'from_commissions_screen',
  'auto',
]);

/**
 * Notes geradas pelo sistema que devem ser sempre ocultadas — são
 * redundantes com os callouts contextuais já exibidos no topo.
 * Notes digitadas por humanos NUNCA caem aqui.
 */
const SYSTEM_NOTES_TO_HIDE = new Set<string>([
  'Defina o método de pagamento na tela Pagamentos antes de confirmar.',
]);

/** Converte snake_case → "Snake case" (capitaliza só a primeira letra). */
function humanizeKey(key: string): string {
  const spaced = key.replace(/[_-]+/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

/** Resolve o label legível de uma chave do payment_details. */
function getPaymentDetailLabel(key: string): string {
  return PAYMENT_DETAILS_LABELS[key] ?? humanizeKey(key);
}

/** Resolve o valor legível de um valor de payment_details. */
function getPaymentDetailValue(value: string): string {
  return PAYMENT_DETAILS_VALUE_LABELS[value] ?? value;
}

// ============================================================================
// HELPERS
// ============================================================================

/** Formata valor monetário em BRL. */
function formatBRL(value: number | null | undefined): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value ?? 0));
}

/** Formata CPF/CNPJ — devolve string mascarada ou null. */
function formatCpfCnpj(cpf?: string | null, cnpj?: string | null): string | null {
  const raw = (cnpj || cpf || '').replace(/\D/g, '');
  if (!raw) return null;
  if (raw.length === 11) return raw.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (raw.length === 14) return raw.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return cnpj || cpf || null;
}

/**
 * Sanitiza nome de cliente vindo da base.
 *
 * Leads frequentemente têm o nome concatenado com telefone (ex.:
 * "ester - (31) 98766-2121"). Esse formato polui a UI quando precisamos
 * truncar — sobra "ester - (31) 9876..." e o usuário não enxerga o nome.
 *
 * Regras:
 *  - Remove tudo após o primeiro " - " (separador típico nome/telefone).
 *  - Remove blocos entre parênteses (DDDs, observações).
 *  - Trim e capitalização da primeira letra.
 *  - Se sobrar vazio, devolve o original.
 */
function cleanCustomerName(raw?: string | null): string | null {
  if (!raw) return null;
  let cleaned = raw.split(' - ')[0] ?? raw;
  cleaned = cleaned.replace(/\([^)]*\)/g, '').trim();
  if (!cleaned) return raw.trim() || null;
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

/** Formata data longa (`dd/MM/yyyy 'às' HH:mm`). */
function formatLong(date: string | null | undefined): string | null {
  if (!date) return null;
  return format(new Date(date), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
}

// ============================================================================
// SUBCOMPONENTE: SectionLabel
// ----------------------------------------------------------------------------
// Estilo Linear/Stripe: micro-label uppercase + ícone pequeno à esquerda.
// Substitui os <h4> simples por algo mais editorial.
// ============================================================================

function SectionLabel({
  icon: Icon,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
      <Icon className="h-3.5 w-3.5" />
      {children}
    </div>
  );
}

// ============================================================================
// SUBCOMPONENTE: Linha "label / valor" com zebra opcional
// ============================================================================

function InfoRow({
  label,
  value,
  className,
}: {
  label: string;
  value: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex justify-between gap-3 px-3 py-2 text-sm rounded-md',
        className,
      )}
    >
      <span className="text-xs text-muted-foreground shrink-0 self-center">{label}</span>
      <span className="text-right text-sm font-medium tabular-nums break-words self-center">
        {value}
      </span>
    </div>
  );
}

// ============================================================================
// SUBCOMPONENTE: Action Bar contextual
// ----------------------------------------------------------------------------
// v1.1.0: padronização em 3 níveis (primária, secundária, destrutiva) com
// alturas/paddings unificados. "Cancelar" vira link discreto à direita,
// numa linha separada, para não competir com as ações principais.
// Apenas UMA primária aparece por status (a esperada).
// ============================================================================

function PayoutActionBar({
  status,
  payoutId,
  onAction,
}: {
  status: PayoutStatus;
  payoutId: string;
  onAction: PayoutDetailSheetProps['onAction'];
}) {
  const canApprove = status === 'pending';
  const canMarkPaid = status === 'pending' || status === 'processing';
  const canFail = status === 'pending' || status === 'processing';
  const canCancel = status === 'pending' || status === 'processing';

  // Estado terminal (paid/failed/cancelled) → não exibe a barra.
  if (!canApprove && !canMarkPaid && !canFail && !canCancel) return null;

  // Define qual ação é PRIMÁRIA por status.
  // - pending     → Iniciar processamento (azul)
  // - processing  → Marcar como pago      (verde)
  const primaryAction: 'approve' | 'mark_paid' | null = canApprove
    ? 'approve'
    : canMarkPaid
      ? 'mark_paid'
      : null;

  // Classe base partilhada por todos os botões para garantir altura/padding/font idênticos.
  const baseBtn = 'h-8 px-3 text-xs font-medium gap-1.5 rounded-md';

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        {/* PRIMÁRIA — Iniciar processamento */}
        {canApprove && primaryAction === 'approve' && (
          <Button
            onClick={() => onAction(payoutId, 'approve')}
            className={cn(
              baseBtn,
              'col-span-2 bg-blue-600 hover:bg-blue-700 text-white shadow-sm',
            )}
          >
            <PlayCircle className="h-3.5 w-3.5" />
            Iniciar processamento
          </Button>
        )}

        {/* PRIMÁRIA — Marcar como pago (só quando já está em processamento) */}
        {canMarkPaid && primaryAction === 'mark_paid' && (
          <Button
            onClick={() => onAction(payoutId, 'mark_paid')}
            className={cn(
              baseBtn,
              'col-span-2 bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm',
            )}
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            Marcar como pago
          </Button>
        )}

        {/* SECUNDÁRIA — Marcar como pago (quando primária já é Iniciar) */}
        {canMarkPaid && primaryAction !== 'mark_paid' && (
          <Button
            variant="outline"
            onClick={() => onAction(payoutId, 'mark_paid')}
            className={cn(baseBtn, 'border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800 dark:border-emerald-900/60 dark:text-emerald-300 dark:hover:bg-emerald-950/40')}
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            Marcar como pago
          </Button>
        )}

        {/* DESTRUTIVA — Marcar como falho */}
        {canFail && (
          <Button
            variant="outline"
            onClick={() => onAction(payoutId, 'mark_failed')}
            className={cn(baseBtn, 'border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive')}
          >
            <XCircle className="h-3.5 w-3.5" />
            Marcar como falho
          </Button>
        )}
      </div>

      {/* Cancelar — link discreto, alinhado à direita, fora do grid. */}
      {canCancel && (
        <div className="flex justify-end pt-0.5">
          <Button
            variant="ghost"
            onClick={() => onAction(payoutId, 'cancel')}
            className="h-7 px-2 text-xs font-normal gap-1 text-muted-foreground hover:text-destructive hover:bg-transparent"
          >
            <Ban className="h-3 w-3" />
            Cancelar pagamento
          </Button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// SUBCOMPONENTE: Card premium de comissão (v1.5.0)
// ----------------------------------------------------------------------------
// Substitui o layout de tabela densa anterior (CommissionTableRowCompact)
// por um card visual coerente com o restante do Sheet (chips de ícone, cards
// arredondados). Hierarquia: chip $ | Negócio + Cliente (lado esquerdo)
//                            valor em destaque + Ref. (lado direito).
// ============================================================================

/**
 * Card individual de comissão renderizado dentro da lista do bloco
 * "Comissões vinculadas" do `PayoutDetailSheet`.
 *
 * Layout (≈ 80px de altura):
 * ┌──┬───────────────────────────────────┬───────────────┐
 * │$ │ NEGÓCIO                           │  R$ 139,00    │
 * │  │ 🧳 #83785347 ↗                    │  Ref. 8606F.. │
 * │  │ 👤 Ester · CPF 797.833.300-04     │               │
 * └──┴───────────────────────────────────┴───────────────┘
 */
function CommissionCardRow({
  commission,
  onOpenDeal,
  onOpenCustomer,
}: {
  commission: PayoutDetailsCommission;
  onOpenDeal?: (dealId: string) => void;
  onOpenCustomer?: (customerId: string) => void;
}) {
  // Documento formatado (CPF/CNPJ) e nome sanitizado.
  const customerDoc = formatCpfCnpj(commission.customer_cpf, commission.customer_cnpj);
  const rawCustomerName = commission.customer_name || commission.lead_name || null;
  const customerName = cleanCustomerName(rawCustomerName);

  // Estado do negócio: removido se temos id mas não temos título.
  const dealRemoved = !!commission.deal_id && !commission.deal_title;
  const dealClickable = !!onOpenDeal && !!commission.deal_id && !dealRemoved;

  // Origem: cliente real (CRM) vs lead (pré-cliente). Usado para badge "Lead".
  const isLeadOrigin = !commission.customer_id && !!commission.lead_name;
  const customerClickable = !!onOpenCustomer && !!commission.customer_id;

  // Tipo de documento para prefixo (CPF/CNPJ).
  const docPrefix = commission.customer_cnpj ? 'CNPJ' : 'CPF';

  return (
    <div
      className={cn(
        // Card base: borda fina, hover sutil, transição suave.
        'group relative flex gap-3 rounded-xl border border-border/60 bg-card p-3',
        'transition-all duration-200 hover:border-border hover:shadow-sm',
        // Borda esquerda colorida — assinatura visual da seção financeira.
        'border-l-[3px] border-l-primary/40 hover:border-l-primary',
      )}
    >
      {/* Chip de ícone — identidade financeira do card. */}
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <CircleDollarSign className="h-5 w-5" />
      </div>

      {/* Bloco central: dados (negócio + cliente). */}
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        {/* Linha 1: Negócio */}
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Negócio
          </span>
          {commission.deal_id ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (dealClickable && commission.deal_id) onOpenDeal!(commission.deal_id);
              }}
              disabled={!dealClickable}
              title={dealRemoved ? 'Negócio removido' : commission.deal_title || undefined}
              className={cn(
                'inline-flex items-center gap-1 font-mono text-xs uppercase min-w-0',
                dealClickable
                  ? 'text-primary hover:underline cursor-pointer'
                  : 'text-muted-foreground cursor-default',
              )}
            >
              <Briefcase className="h-3 w-3 shrink-0" />
              <span className="truncate">
                #{commission.deal_id.substring(0, 8).toUpperCase()}
              </span>
              {dealClickable && (
                <ExternalLink className="h-2.5 w-2.5 opacity-60 shrink-0" />
              )}
            </button>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </div>

        {/* Linha 2: Cliente — v1.5.1: paridade de affordance com o Negócio.
            Quando clicável, aplica cor primary + hover:underline + ícone ↗,
            deixando claro que abre o modal do cliente. */}
        <div className="flex items-center gap-2 min-w-0">
          <UserIcon
            className={cn(
              'h-3.5 w-3.5 shrink-0',
              customerClickable ? 'text-primary' : 'text-muted-foreground',
            )}
          />
          {customerName ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (customerClickable && commission.customer_id) {
                  onOpenCustomer!(commission.customer_id);
                }
              }}
              disabled={!customerClickable}
              title={rawCustomerName ?? undefined}
              className={cn(
                'inline-flex items-center gap-1 min-w-0 text-left',
                customerClickable
                  ? 'text-primary hover:underline cursor-pointer'
                  : 'cursor-default',
              )}
            >
              <span
                className={cn(
                  'text-sm font-medium truncate',
                  customerClickable ? 'text-primary' : 'text-foreground',
                )}
              >
                {customerName}
              </span>
              {customerClickable && (
                <ExternalLink className="h-2.5 w-2.5 opacity-60 shrink-0" />
              )}
              {isLeadOrigin && (
                <span className="shrink-0 rounded-md bg-warning/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-warning">
                  Lead
                </span>
              )}
            </button>
          ) : (
            <span className="text-sm text-muted-foreground">—</span>
          )}
        </div>

        {/* Linha 3: Documento (se houver) */}
        {customerDoc && (
          <div className="text-[11px] text-muted-foreground tabular-nums pl-5">
            {docPrefix} {customerDoc}
          </div>
        )}
      </div>

      {/* Bloco direito: valor + referência. */}
      <div className="flex shrink-0 flex-col items-end justify-between gap-1.5">
        <span className="text-base font-bold tabular-nums text-foreground leading-none">
          {formatBRL(commission.reward_value)}
        </span>
        <span
          className="font-mono text-[10px] text-muted-foreground/80 tracking-wide"
          title={`Ref. ${commission.id}`}
        >
          Ref. {commission.id.substring(0, 8).toUpperCase()}
        </span>
      </div>
    </div>
  );
}

// ============================================================================
// SUBCOMPONENTE: Timeline cronológica (lê de action_logs — auditoria)
// ----------------------------------------------------------------------------
// v2.0.0 (2026-04-30): migrado de campos do payout para `action_logs`,
// passando a exibir QUEM executou cada evento.
// ============================================================================

/**
 * Mapeamento visual por tipo de ação (cor, ícone, label PT-BR).
 * Mantido fora do componente para evitar recriação a cada render.
 */
const ACTION_VISUAL: Record<
  PayoutActionLog['action'],
  {
    icon: React.ComponentType<{ className?: string }>;
    color: string;
    iconBg: string;
    label: string;
  }
> = {
  created:   { icon: Clock,        color: 'text-amber-600 dark:text-amber-400',     iconBg: 'bg-amber-100 dark:bg-amber-950/40',     label: 'Saque solicitado' },
  approved:  { icon: Loader2,      color: 'text-blue-600 dark:text-blue-400',       iconBg: 'bg-blue-100 dark:bg-blue-950/40',       label: 'Pagamento aprovado / em processamento' },
  paid:      { icon: CheckCircle2, color: 'text-emerald-600 dark:text-emerald-400', iconBg: 'bg-emerald-100 dark:bg-emerald-950/40', label: 'Pagamento confirmado' },
  failed:    { icon: XCircle,      color: 'text-rose-600 dark:text-rose-400',       iconBg: 'bg-rose-100 dark:bg-rose-950/40',       label: 'Pagamento falhou' },
  cancelled: { icon: Ban,          color: 'text-muted-foreground',                  iconBg: 'bg-muted',                              label: 'Pagamento cancelado' },
  updated:   { icon: Info,         color: 'text-muted-foreground',                  iconBg: 'bg-muted',                              label: 'Pagamento atualizado' },
};

/**
 * Componente PayoutTimeline (v2.0.0)
 * --------------------------------------------------------------------------
 * Renderiza a timeline a partir de `action_logs` retornado pela RPC
 * `get_referral_payout_details`. Cada evento mostra:
 *   - Ação (label PT-BR + ícone + cor)
 *   - Usuário que executou (nome + email) — auditoria
 *   - Motivo / observações quando aplicável
 *   - ID externo da transação quando aplicável
 *   - Data/hora formatada
 *
 * Os logs são gravados automaticamente pelo trigger `trg_log_payout_action`,
 * garantindo que toda mudança de status (aprovar, pagar, falhar, cancelar)
 * seja rastreável por usuário responsável.
 */
function PayoutTimeline({
  actionLogs,
}: {
  actionLogs: PayoutActionLog[];
}) {
  // Ordena cronologicamente do mais antigo → mais novo (igual ContractTimeline).
  const events = React.useMemo(
    () =>
      [...actionLogs].sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      ),
    [actionLogs],
  );

  if (events.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">Nenhum evento registrado.</p>
    );
  }

  return (
    <ol className="relative space-y-4 pl-1">
      {events.map((ev, idx) => {
        const visual = ACTION_VISUAL[ev.action] ?? ACTION_VISUAL.updated;
        const Icon = visual.icon;
        const isLast = idx === events.length - 1;

        // Resolve nome do usuário que executou — "Sistema" como fallback.
        const performerLabel =
          ev.performer_name?.trim() ||
          ev.performer_email?.trim() ||
          'Sistema';

        return (
          <li key={ev.id} className="relative flex gap-3">
            {/* Conector vertical entre os pontos da timeline */}
            {!isLast && (
              <span
                aria-hidden
                className="absolute left-[13px] top-7 bottom-[-12px] w-px bg-border/60"
              />
            )}
            {/* Ícone redondo */}
            <span
              className={cn(
                'flex h-7 w-7 shrink-0 items-center justify-center rounded-full ring-4 ring-background',
                visual.iconBg,
              )}
            >
              <Icon className={cn('h-3.5 w-3.5', visual.color)} />
            </span>

            {/* Conteúdo do evento */}
            <div className="flex-1 min-w-0 pt-0.5">
              <p className={cn('text-sm font-semibold leading-tight', isLast && 'text-foreground')}>
                {visual.label}
              </p>

              {/* Auditoria: quem executou esta ação */}
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed flex items-center gap-1 flex-wrap">
                <UserIcon className="h-3 w-3 shrink-0" />
                <span>por</span>
                <span className="font-medium text-foreground">{performerLabel}</span>
                {ev.performer_email && ev.performer_name && (
                  <span className="text-muted-foreground/70">
                    ({ev.performer_email})
                  </span>
                )}
              </p>

              {/* Motivo (failed_reason) ou observação manual */}
              {(ev.reason || ev.notes) && (
                <p className="text-xs text-muted-foreground mt-1 break-words leading-relaxed">
                  {ev.reason && (
                    <>
                      <span className="font-medium text-foreground/80">Motivo:</span>{' '}
                      {ev.reason}
                    </>
                  )}
                  {ev.reason && ev.notes && <br />}
                  {ev.notes}
                </p>
              )}

              {/* ID externo da transação — só relevante em "paid" */}
              {ev.external_transaction_id && (
                <p className="text-[11px] text-muted-foreground mt-1 font-mono break-all">
                  ID transação: {ev.external_transaction_id}
                </p>
              )}

              {/* Data/hora formatada */}
              <p className="text-[11px] text-muted-foreground/70 mt-1 tabular-nums">
                {formatLong(ev.created_at)}
              </p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

// ============================================================================
// COMPONENTE PRINCIPAL — PayoutDetailSheet
// ============================================================================

export function PayoutDetailSheet({
  payoutId,
  onClose,
  onAction,
  onOpenDeal,
  onOpenCustomer,
}: PayoutDetailSheetProps) {
  const open = !!payoutId;
  const { data, isLoading } = useReferralPayoutDetails(payoutId, open);

  const payout = data?.payout;
  const affiliate = data?.affiliate;
  const commissions = data?.commissions ?? [];

  const statusInfo = payout
    ? STATUS_CONFIG[payout.status] || STATUS_CONFIG.pending
    : null;

  // ────────────────────────────────────────────────────────────────────────
  // Pré-processamento de payment_details (v1.1.0)
  // ----------------------------------------------------------------------
  // Separa o JSONB em 3 grupos para renderização específica:
  //   - `note`               → callout amber (texto natural).
  //   - `source`             → badge contextual.
  //   - `no_pix_on_affiliate` (chave OU valor) → aviso amigável quando o
  //                            método é PIX e o afiliado não tem chave.
  //   - demais entries       → linhas label/valor com tradução amigável.
  // ────────────────────────────────────────────────────────────────────────
  const paymentDetailsProcessed = React.useMemo(() => {
    const pd = payout?.payment_details;
    const empty = {
      note: null as string | null,
      source: null as string | null,
      missingPix: false,
      others: [] as Array<[string, string]>,
      audit: [] as Array<[string, string]>,
    };
    if (!pd || typeof pd !== 'object') return empty;

    const entries = Object.entries(pd as Record<string, unknown>).filter(
      ([, v]) => v !== null && v !== undefined && String(v).trim() !== '',
    );

    const result = { ...empty, others: [] as Array<[string, string]>, audit: [] as Array<[string, string]>};
    for (const [k, v] of entries) {
      const value = typeof v === 'object' ? JSON.stringify(v) : String(v);

      // 1) Note do sistema → ocultar incondicionalmente; manual → mantém.
      if (k === 'note') {
        if (!SYSTEM_NOTES_TO_HIDE.has(value)) {
          result.note = value;
        }
        continue;
      }

      // 2) Source: sentinels viram apenas flag (não exibem "Origem").
      if (k === 'source') {
        if (SENTINEL_VALUES.has(value)) {
          if (value === 'no_pix_on_affiliate') result.missingPix = true;
          // Sentinel ainda assim cai em audit para rastreio.
          result.audit.push([k, value]);
        } else {
          result.source = getPaymentDetailValue(value);
        }
        continue;
      }

      // 3) Flag explícita de PIX ausente (chave booleana legada).
      if (k === 'no_pix_on_affiliate' || value === 'no_pix_on_affiliate') {
        result.missingPix = true;
        result.audit.push([k, value]);
        continue;
      }

      // 4) Allowlist: chaves seguras vão para a lista principal.
      if (DISPLAYABLE_PD_KEYS.has(k)) {
        // ───────────────────────────────────────────────────────────────
        // Deduplicação de PIX (v1.2.0)
        // ----------------------------------------------------------------
        // O snapshot `payment_details` costuma replicar `pix_key` e
        // `pix_key_type` que JÁ são exibidos na linha-header (vinda do
        // `affiliate` ao vivo). Quando o valor histórico é idêntico ao
        // atual, omitimos para não duplicar visualmente. Quando difere,
        // mantemos para preservar a rastreabilidade do que foi gerado.
        // ───────────────────────────────────────────────────────────────
        if (k === 'pix_key' && affiliate?.pix_key && value === affiliate.pix_key) {
          continue;
        }
        if (k === 'pix_key_type' && affiliate?.pix_key_type && value === affiliate.pix_key_type) {
          continue;
        }
        result.others.push([k, value]);
      } else {
        // Demais chaves → bloco recolhido de auditoria.
        result.audit.push([k, value]);
      }
    }
    return result;
  }, [payout?.payment_details, affiliate?.pix_key, affiliate?.pix_key_type]);

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="!w-full sm:!w-[480px] sm:!max-w-[480px] p-0 overflow-x-hidden bg-background">
        {isLoading || !payout ? (
          <div className="p-6 space-y-4">
            <Skeleton className="h-9 w-9 rounded-xl" />
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : (
          <ScrollArea className="h-full">
            {/* ============== Header com gradient sutil ============== */}
            <div className="relative bg-gradient-to-b from-muted/50 via-muted/15 to-transparent px-5 sm:px-6 pt-6 pb-5">
              <SheetHeader className="space-y-3 text-left">
                <div className="flex items-start gap-3">
                  {/* Chip do ícone */}
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/15">
                    <Wallet className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <SheetTitle className="text-base font-semibold leading-tight truncate">
                      {affiliate?.full_name ?? 'Pagamento'}
                    </SheetTitle>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[11px] text-muted-foreground font-mono uppercase">
                        #{payout.id.substring(0, 8).toUpperCase()}
                      </span>
                      {statusInfo && (
                        <>
                          <span className="text-muted-foreground/50">·</span>
                          {/* Status no formato unificado: bolinha colorida + texto */}
                          <PayoutStatusBadge status={payout.status} />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </SheetHeader>
            </div>

            {/* ============== Corpo ============== */}
            <div className="px-5 sm:px-6 pb-8 space-y-7">
              {/* ============== Action Bar ============== */}
              <PayoutActionBar
                status={payout.status}
                payoutId={payout.id}
                onAction={onAction}
              />

              {/* ============== Bloco: Informações gerais ============== */}
              <section className="space-y-3">
                <SectionLabel icon={Info}>Informações gerais</SectionLabel>
                <div className="rounded-xl border border-border/60 bg-card overflow-hidden">
                  <InfoRow
                    label="Valor"
                    value={
                      <span className="text-base font-semibold tabular-nums text-foreground">
                        {formatBRL(payout.amount)}
                      </span>
                    }
                  />
                  <InfoRow
                    label="Método"
                    value={METHOD_LABEL[payout.payout_method] ?? payout.payout_method}
                    className="bg-muted/20"
                  />
                  <InfoRow label="Moeda" value={payout.currency} />
                  <InfoRow
                    label="Comissões"
                    value={payout.commissions_count}
                    className="bg-muted/20"
                  />
                  <InfoRow
                    label="Solicitado em"
                    value={formatLong(payout.requested_at) ?? '—'}
                  />
                  {payout.processed_at && (
                    <InfoRow
                      label="Processado em"
                      value={formatLong(payout.processed_at)}
                      className="bg-muted/20"
                    />
                  )}
                  {payout.paid_at && (
                    <InfoRow label="Pago em" value={formatLong(payout.paid_at)} />
                  )}
                  {payout.external_transaction_id && (
                    <InfoRow
                      label="ID transação"
                      value={
                        <span className="font-mono text-xs break-all">
                          {payout.external_transaction_id}
                        </span>
                      }
                      className="bg-muted/20"
                    />
                  )}
                </div>

                {/* Falha em destaque */}
                {payout.failed_reason && payout.status === 'failed' && (
                  <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-3 flex gap-2.5">
                    <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                    <div className="text-xs leading-relaxed">
                      <p className="font-semibold text-destructive">Motivo da falha</p>
                      <p className="text-muted-foreground mt-1">
                        {payout.failed_reason}
                      </p>
                    </div>
                  </div>
                )}

                {/* Observações do payout */}
                {payout.notes && (
                  <div className="rounded-xl border border-border/60 bg-card p-3 flex gap-2.5">
                    <StickyNote className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                    <p className="text-xs whitespace-pre-wrap break-words leading-relaxed">
                      {payout.notes}
                    </p>
                  </div>
                )}
              </section>

              {/* ============== Bloco: Dados de pagamento ============== */}
              <section className="space-y-3">
                <SectionLabel icon={CreditCard}>Dados de pagamento</SectionLabel>

                {/*
                 * Aviso contextual de PIX (v1.1.1)
                 * --------------------------------------------------------
                 * O snapshot `payment_details.source = no_pix_on_affiliate`
                 * é gravado NA HORA da geração do payout. Ou seja, ele
                 * representa o estado do afiliado naquele momento — não
                 * o estado atual. Por isso checamos sempre o `affiliate`
                 * vivo antes de exibir um alerta:
                 *   - Se HOJE o afiliado realmente não tem PIX → alerta
                 *     âmbar (bloqueio operacional real).
                 *   - Se já cadastrou DEPOIS → info verde discreta,
                 *     reconciliando o histórico com o estado atual.
                 */}
                {paymentDetailsProcessed.missingPix && !affiliate?.pix_key && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 flex gap-2.5 dark:border-amber-900/60 dark:bg-amber-950/30">
                    <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                    <div className="text-xs leading-relaxed">
                      <p className="font-semibold text-amber-800 dark:text-amber-300">
                        Afiliado sem chave PIX cadastrada
                      </p>
                      <p className="text-amber-700/80 dark:text-amber-400/80 mt-1">
                        Defina o método e os dados de pagamento na tela de Pagamentos do afiliado antes de confirmar o saque.
                      </p>
                    </div>
                  </div>
                )}

                {paymentDetailsProcessed.missingPix && affiliate?.pix_key && (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 flex gap-2.5 dark:border-emerald-900/60 dark:bg-emerald-950/30">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                    <div className="text-xs leading-relaxed">
                      <p className="font-semibold text-emerald-800 dark:text-emerald-300">
                        Chave PIX já cadastrada
                      </p>
                      <p className="text-emerald-700/80 dark:text-emerald-400/80 mt-1">
                        Quando este pagamento foi gerado o afiliado ainda não tinha PIX. Use a chave atual exibida abaixo para confirmar o saque.
                      </p>
                    </div>
                  </div>
                )}

                {/* Card consolidado: PIX + entries traduzidas + origem + observação */}
                <div className="rounded-xl border border-border/60 bg-card overflow-hidden">
                  {affiliate?.pix_key && (
                    <InfoRow
                      label={`Chave PIX${affiliate.pix_key_type ? ` (${affiliate.pix_key_type.toUpperCase()})` : ''}`}
                      value={
                        <span className="font-mono text-xs break-all">
                          {affiliate.pix_key}
                        </span>
                      }
                    />
                  )}

                  {paymentDetailsProcessed.others.map(([k, v], idx) => (
                    <InfoRow
                      key={k}
                      label={getPaymentDetailLabel(k)}
                      value={
                        <span className="font-mono text-xs break-all">
                          {getPaymentDetailValue(v)}
                        </span>
                      }
                      className={cn(
                        // Zebra alternando — começa após o PIX (se houver)
                        ((affiliate?.pix_key ? idx + 1 : idx) % 2 === 1) && 'bg-muted/20',
                      )}
                    />
                  ))}

                  {paymentDetailsProcessed.source && (
                    <InfoRow
                      label="Origem"
                      value={
                        <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                          <Sparkles className="h-3 w-3" />
                          {paymentDetailsProcessed.source}
                        </span>
                      }
                    />
                  )}

                  {/* Empty state: nenhum dado relevante */}
                  {!affiliate?.pix_key &&
                    paymentDetailsProcessed.others.length === 0 &&
                    !paymentDetailsProcessed.source && (
                      <div className="flex flex-col items-center justify-center py-6 px-3 text-center">
                        <CreditCard className="h-8 w-8 text-muted-foreground/30 mb-2" />
                        <p className="text-xs text-muted-foreground">
                          Nenhum dado de pagamento registrado.
                        </p>
                      </div>
                    )}
                </div>

                {/*
                 * Observação extraída do JSONB → callout dedicado.
                 * v1.1.1: oculta a mensagem-padrão do sentinel
                 * `no_pix_on_affiliate` quando o afiliado JÁ tem PIX hoje
                 * — evita ruído contraditório com o aviso verde acima.
                 * Notes manuais (digitadas por humano) seguem aparecendo.
                 */}
                {paymentDetailsProcessed.note && (
                  <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-3 flex gap-2.5 dark:border-blue-900/60 dark:bg-blue-950/30">
                    <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
                    <p className="text-xs text-blue-900/90 dark:text-blue-200/90 leading-relaxed">
                      {paymentDetailsProcessed.note}
                    </p>
                  </div>
                )}

                {/*
                 * Bloco de auditoria recolhido (v1.2.0)
                 * Mostra chaves do snapshot que não fazem parte da allowlist
                 * principal — preserva rastreabilidade sem poluir a UI.
                 */}
                {paymentDetailsProcessed.audit.length > 0 && (
                  <details className="group rounded-xl border border-border/60 bg-card/40 px-3 py-2 text-xs">
                    <summary className="cursor-pointer select-none text-[11px] uppercase tracking-wider font-medium text-muted-foreground flex items-center gap-1.5 hover:text-foreground transition-colors">
                      <History className="h-3 w-3" />
                      Detalhes técnicos (auditoria)
                      <span className="ml-auto text-muted-foreground/60 font-normal normal-case tracking-normal">
                        {paymentDetailsProcessed.audit.length} {paymentDetailsProcessed.audit.length === 1 ? 'item' : 'itens'}
                      </span>
                    </summary>
                    <div className="mt-2 space-y-1 pt-2 border-t border-border/40">
                      {paymentDetailsProcessed.audit.map(([k, v]) => (
                        <div key={k} className="flex justify-between gap-3 py-1">
                          <span className="text-muted-foreground font-mono">{k}</span>
                          <span className="font-mono text-right break-all text-foreground/80">{v}</span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </section>

              {/* ============== Bloco: Afiliado ============== */}
              <section className="space-y-3">
                <SectionLabel icon={UserIcon}>Afiliado</SectionLabel>
                <div className="rounded-xl border border-border/60 bg-card p-3.5 space-y-2">
                  {affiliate ? (
                    <>
                      {/* Linha do nome + ID alinhado à direita */}
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-semibold truncate">
                          {affiliate.full_name ?? '—'}
                        </p>
                        {affiliate.id && (
                          <p className="text-[11px] text-muted-foreground/70 flex items-center gap-1.5 shrink-0">
                            <KeyRound className="h-3 w-3" />
                            <span>ID:</span>
                            <span className="font-mono uppercase">
                              {affiliate.id.substring(0, 8).toUpperCase()}
                            </span>
                          </p>
                        )}
                      </div>
                      {affiliate.email && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                          <Mail className="h-3.5 w-3.5" />
                          {affiliate.email}
                        </p>
                      )}
                    </>
                  ) : (
                    /* Fallback v1.2.0: payout sem afiliado vinculado (excluído). */
                    <p className="text-sm italic text-muted-foreground flex items-center gap-1.5">
                      <AlertCircle className="h-4 w-4" />
                      Afiliado removido
                    </p>
                  )}
                </div>
              </section>

              {/* ============== Bloco: Comissões vinculadas (v1.5.0) ============== */}
              {/*
                Layout em lista de cards premium (substitui a tabela densa anterior):
                - Header da seção com badge de contagem à direita.
                - Container externo: card grande com gradiente sutil, alinhado
                  ao padrão visual dos outros blocos (Afiliado, Pagamento).
                - Lista interna com altura fixa (max-h-[320px]) + scroll suave.
                - Rodapé sticky-bottom com soma total das comissões.
              */}
              <section className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <SectionLabel icon={CircleDollarSign}>
                    Comissões vinculadas
                  </SectionLabel>
                  {commissions.length > 0 && (
                    <Badge
                      variant="secondary"
                      className="rounded-full px-2.5 py-0.5 text-[10px] font-semibold"
                    >
                      {commissions.length}{' '}
                      {commissions.length === 1 ? 'transação' : 'transações'}
                    </Badge>
                  )}
                </div>

                {commissions.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-border/60 p-8 text-center">
                    <CircleDollarSign className="mx-auto mb-2 h-6 w-6 text-muted-foreground/50" />
                    <p className="text-xs text-muted-foreground">
                      Nenhuma comissão vinculada a este pagamento.
                    </p>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-border/60 bg-gradient-to-b from-card to-muted/10 overflow-hidden">
                    {/* Lista rolável de cards */}
                    <div className="max-h-[320px] overflow-y-auto scrollbar-thin p-2 space-y-2">
                      {commissions.map((c) => (
                        <CommissionCardRow
                          key={c.id}
                          commission={c}
                          onOpenDeal={onOpenDeal}
                          onOpenCustomer={onOpenCustomer}
                        />
                      ))}
                    </div>

                    {/* Rodapé fixo (não rola) com total agregado */}
                    <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-t border-border/60 bg-muted/30 backdrop-blur-sm">
                      <span className="text-[11px] text-muted-foreground">
                        {commissions.length}{' '}
                        {commissions.length === 1 ? 'comissão' : 'comissões'}{' '}
                        nesse pagamento
                      </span>
                      <span className="text-[11px] text-muted-foreground tabular-nums">
                        Total{' '}
                        <span className="text-base font-bold text-primary ml-1">
                          {formatBRL(
                            commissions.reduce(
                              (acc, c) => acc + (Number(c.reward_value) || 0),
                              0,
                            ),
                          )}
                        </span>
                      </span>
                    </div>
                  </div>
                )}
              </section>

              {/* ============== Bloco: Anexos / Comprovantes ============== */}
              <section className="space-y-3">
                <PayoutAttachmentsManager payoutId={payout.id} />
              </section>

              {/* ============== Bloco: Timeline ============== */}
              <section className="space-y-4">
                <SectionLabel icon={History}>Histórico</SectionLabel>
                <PayoutTimeline actionLogs={data?.action_logs ?? []} />
              </section>
            </div>
          </ScrollArea>
        )}
      </SheetContent>
    </Sheet>
  );
}
